import React from 'react';
import RowTextField from './fields/RowTextField';
import RowTextAreaField from './fields/RowTextAreaField';
import RowPickListField from './fields/RowPickListField';
import RowReferenceField from './fields/RowReferenceField';
import RowOwnerField from './fields/RowOwnerField';
import RowBooleanField from './fields/RowBooleanField';
import RowDateField from './fields/RowDateField';
import RowEmailField from './fields/RowEmailField';
import RowDateTimeField from './fields/RowDateTimeField';
import RowMultipicklistField from './fields/RowMultipicklistField';
import RowTimeField from './fields/RowTimeField';

const FormField = ({ field, data, ...props }) => {
  // Use fieldname as primary, fallback to name
  const fieldName = field.fieldname || field.name;
  
  
  // Determine if field is mandatory (1 = mandatory, 0 = optional)
  const isMandatory = field.mandatory === 1 || field.mandatory === '1' || field.mandatory === true;
  
  const commonProps = {
    fieldLabel: field.label,
    fieldName: fieldName,
    fieldMandatory: isMandatory,
    fieldType: field.type,
    fieldUitype: field.uitype,
    data,
    ...props,
  };

  // Ensure the field name is properly set for form submission

  // Use uitype as primary field type identifier (vtiger standard)
  const uitype = parseInt(field.uitype) || field.uitype;
  const fieldType = field.type;


  // Handle string-based uitypes first
  if (typeof field.uitype === 'string' && isNaN(parseInt(field.uitype))) {
    
    switch (field.uitype.toLowerCase()) {
      case 'string':
        return <RowTextField {...commonProps} />;
      case 'text':
        return <RowTextAreaField {...commonProps} />;
      case 'email':
        return <RowEmailField {...commonProps} />;
      case 'phone':
        return <RowTextField {...commonProps} />;
      case 'url':
        return <RowTextField {...commonProps} />;
      case 'date':
        return <RowDateField {...commonProps} />;
      case 'datetime':
        return <RowDateTimeField {...commonProps} />;
      case 'time':
        return <RowTimeField {...commonProps} />;
      case 'boolean':
        return <RowBooleanField {...commonProps} />;
      case 'picklist':
        return (
          <RowPickListField 
            {...commonProps} 
            pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
          />
        );
      case 'multipicklist':
        return (
          <RowMultipicklistField 
            {...commonProps} 
            pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
          />
        );
      case 'reference':
        return (
          <RowReferenceField 
            {...commonProps} 
            referenceModule={field.referencemodules?.[0] || field.type_info?.refersTo?.[0]} 
            referenceModules={field.referencemodules || field.type_info?.refersTo || []}
          />
        );
      case 'owner':
        return (
          <RowOwnerField 
            {...commonProps} 
            UsersPickListValues={field.picklistvalues?.Users || field.type_info?.picklistValues?.Users || {}}
            GroupPickListValues={field.picklistvalues?.Groups || field.type_info?.picklistValues?.Groups || {}}
          />
        );
      case 'textarea':
        return <RowTextAreaField {...commonProps} />;
      default:
        return <RowTextField {...commonProps} />;
    }
  }

  switch (uitype) {
    // Text fields
    case 1:   // Text
    case '1':
      return <RowTextField {...commonProps} />;
    
    // Number fields
    case 7:   // Number
    case '7':
    case 9:   // Percentage
    case '9':
      return <RowTextField {...commonProps} />;
    
    // Reference/Lookup fields
    case 10:  // Reference
    case '10':
      return (
        <RowReferenceField 
          {...commonProps} 
          referenceModule={field.referencemodules?.[0] || field.type_info?.refersTo?.[0]} 
          referenceModules={field.referencemodules || field.type_info?.refersTo || []}
        />
      );
    
    // Phone fields
    case 11:  // Phone
    case '11':
      return <RowTextField {...commonProps} />;
    
    // Email fields
    case 13:  // Email
    case '13':
      return <RowEmailField {...commonProps} />;
    
    // Time fields
    case 14:  // Time
    case '14':
      return <RowTimeField {...commonProps} />;
    
    // Picklist fields
    case 15:  // Picklist
    case '15':
    case 16:  // Picklist
    case '16':
      return (
        <RowPickListField 
          {...commonProps} 
          pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
        />
      );
    
    // URL fields
    case 17:  // URL
    case '17':
      return <RowTextField {...commonProps} />;
    
    // Textarea fields
    case 19:  // Text Area
    case '19':
    case 21:  // Text Area
    case '21':
      return <RowTextAreaField {...commonProps} />;
    
    // Multi-picklist fields
    case 33:  // Multi-select picklist
    case '33':
      return (
        <RowMultipicklistField 
          {...commonProps} 
          pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
        />
      );
    
    // Owner fields
    case 53:  // Owner
    case '53':
      return (
        <RowOwnerField 
          {...commonProps} 
          UsersPickListValues={field.picklistvalues?.Users || field.type_info?.picklistValues?.Users || {}}
          GroupPickListValues={field.picklistvalues?.Groups || field.type_info?.picklistValues?.Groups || {}}
        />
      );
    
    // Salutation fields
    case 55:  // Salutation
    case '55':
      return (
        <RowPickListField 
          {...commonProps} 
          pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
        />
      );
    
    // Boolean/Checkbox fields
    case 56:  // Boolean
    case '56':
      return <RowBooleanField {...commonProps} />;
    
    // Date fields
    case 5:   // Date
    case '5':
      return <RowDateField {...commonProps} />;
    
    // DateTime fields
    case 70:  // DateTime
    case '70':
      return <RowDateTimeField {...commonProps} />;
    
    // Currency fields
    case 71:  // Currency
    case '71':
      return <RowTextField {...commonProps} />;
    
    // Image fields (treat as file upload or text for now)
    case 69:  // Image
    case '69':
      return <RowTextField {...commonProps} />;
    
    // Document fields (treat as text for now)
    case 68:  // Document
    case '68':
      return <RowTextField {...commonProps} />;
    
    // Auto-generated fields (read-only text)
    case 4:   // Auto-generated
    case '4':
      return <RowTextField {...commonProps} />;
    
    // Skype fields (treat as text)
    case 85:  // Skype
    case '85':
      return <RowTextField {...commonProps} />;
    
    // File fields (treat as text for now)
    case 28:  // File
    case '28':
      return <RowTextField {...commonProps} />;
    
    // Password fields
    case 99:  // Password
    case '99':
      return (
        <RowTextField 
          {...commonProps} 
          inputType="password"
        />
      );
    
    // Fallback for string-based field types (backward compatibility)
    case 'string':
    case 'text':
    case 'phone':
    case 'url':
    case 'integer':
    case 'double':
    case 'currency':
    case 'salutation':
      return <RowTextField {...commonProps} />;
    
    case 'textarea':
      return <RowTextAreaField {...commonProps} />;
    
    case 'picklist':
      return (
        <RowPickListField 
          {...commonProps} 
          pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
        />
      );
    
    case 'multipicklist':
      return (
        <RowMultipicklistField 
          {...commonProps} 
          pickListValues={field.picklistvalues || field.type_info?.picklistValues || {}} 
        />
      );
    
    case 'reference':
      return (
        <RowReferenceField 
          {...commonProps} 
          referenceModule={field.referencemodules?.[0] || field.type_info?.refersTo?.[0]} 
          referenceModules={field.referencemodules || field.type_info?.refersTo || []}
        />
      );
    
    case 'owner':
      return (
        <RowOwnerField 
          {...commonProps} 
          UsersPickListValues={field.picklistvalues?.Users || field.type_info?.picklistValues?.Users || {}}
          GroupPickListValues={field.picklistvalues?.Groups || field.type_info?.picklistValues?.Groups || {}}
        />
      );
    
    case 'boolean':
      return <RowBooleanField {...commonProps} />;
    
    case 'date':
      return <RowDateField {...commonProps} />;
    
    case 'email':
      return <RowEmailField {...commonProps} />;
    
    case 'datetime':
      return <RowDateTimeField {...commonProps} />;
    
    case 'time':
      return <RowTimeField {...commonProps} />;
    
    default:
      // Fallback to text field for unknown types
      return <RowTextField {...commonProps} />;
  }
};

export default FormField;